import { proj4 } from "../../util/geo";

type Coordinates = [number, number];
type BBOX = [number, number, number, number];
const inBounds = (coordinates: Coordinates | number[], bbox: BBOX | number[]) =>
  coordinates[0] >= bbox[0] &&
  coordinates[0] <= bbox[1] &&
  coordinates[1] >= bbox[2] &&
  coordinates[1] <= bbox[3];

const mapBounds = {
  NL: { bbox: [0, 300000, 300000, 700000], srid: "EPSG:28992" },
};

export interface Result {
  location: { lat: number; lng: number };
  exact?: boolean;
  zoom: number;
}

export interface Suggest {
  placeId: string;
  description: string;
  label: string;
  location: number[];
  zoom: number;
  matchedSubstrings: [{ offset: number; length: number }];
  type: "location" | "zone";
}

interface Plugin {
  geocode(p: string): Promise<Result | undefined>;
  suggests(input: string, props: any): Promise<Suggest[] | undefined>;
}

class GPSPlugin implements Plugin {
  async geocode(p: string): Promise<Result | undefined> {
    const m = /^(GPS|XY) ([+-]?[\d.]+)[,\s]+([+-]?[\d.]+)/.exec(p);
    if (!m) return;
    let coordinates: number[] = [parseFloat(m[2]), parseFloat(m[3])];
    if (m[1] === "XY") {
      const country = mapBounds.NL;
      coordinates = proj4(country.srid).inverse(coordinates).reverse();
    }
    return {
      location: { lat: coordinates[0], lng: coordinates[1] },
      exact: true,
      zoom: 15,
    };
  }
  async suggests(input: string, _props: any) {
    if (input && /^(GPS\s?)?[-\d\s,.]*$/.exec(input)) {
      const m = /(?:GPS\s?)?([-\d.]+)[,\s]+([-\d.]+)/.exec(input);
      if (!m) return;
      let coordinates: number[] = [parseFloat(m[1]), parseFloat(m[2])];
      const country = mapBounds.NL;
      let location;
      if (inBounds(coordinates, [-90, 90, -180, 180])) {
        location = "GPS " + coordinates.map((n) => n.toFixed(5)).join(",");
      } else if (country && inBounds(coordinates, country.bbox)) {
        location = "XY " + coordinates.map((n) => n.toFixed()).join(",");
        coordinates = proj4(country.srid).inverse(coordinates).reverse();
      }
      if (location) {
        const result: Suggest = {
          placeId: location,
          description: location,
          label: location,
          location: coordinates,
          zoom: 15,
          matchedSubstrings: [{ offset: 0, length: 0 }],
          type: "location",
        };
        return [result];
      }
    }
    // if (!/^\d{4}([ \w]|\b)/.test(input)) return {}; // do not capture zipcodes
  }
}

class PDOKPlugin implements Plugin {
  // see https://github.com/PDOK/locatieserver/wiki/API-Locatieserver
  async geocode(p: string) {
    if (p && p.startsWith("pdok-")) {
      return fetch("https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup?id=" + p.substring(5))
        .then((response) => response.json())
        .then(({ response }) => {
          if (response.numFound > 0) {
            const result = response.docs[0];
            const m = /([\d.]+) ([\d.]+)/.exec(result.centroide_ll);
            if (m) {
              return {
                location: { lat: parseFloat(m[2]), lng: parseFloat(m[1]) },
                exact: result.type === "adres",
                zoom: 15,
              };
            }
          }
          throw new Error("No results");
        });
    }
  }

  async suggests(input: string, props: any) {
    if (input?.length <= 2) return;
    const loc = props.location;
    return fetch(
      "https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?fq=-type:gemeente&q=" +
        input +
        (loc ? `&lat=${loc.lat}&lon=${loc.lng}` : "")
    )
      .then((response) => response.json())
      .then((data) =>
        data.response.docs.map((s) => ({
          placeId: `pdok-${s.id}`,
          description: s.weergavenaam,
          label: data.highlighting[s.id],
          zoom: 15,
          matchedSubstrings: [{ offset: 0, length: 0 }],
          type: s.type === "adres" ? "location" : "zone",
        }))
      );
  }
}

export { GPSPlugin, PDOKPlugin };
